// src/pages/TradeStatistics.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { useAuth } from "../context/AuthContext";
import { FaSyncAlt, FaSearch, FaSort, FaSortUp, FaSortDown, FaDownload, FaChevronRight, FaChevronDown, FaChartLine } from "react-icons/fa";

/* ---------------- helpers ---------------- */
const pad2 = (n) => String(n).padStart(2, "0");
const getTodayLocalISODate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const nf2 = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
const formatNum = (v) => nf2.format(safeNum(v));

const PERIODS = [
    { key: "period_30s", label: "30s" },
    { key: "period_1m", label: "1m" },
    { key: "period_3m", label: "3m" },
    { key: "period_5m", label: "5m" },
    { key: "period_10m", label: "10m" },
    { key: "period_1h", label: "1h" },
];

const PERIOD_KEYS = PERIODS.map((p) => p.key);

const getPeriodObj = (row, periodKey) => row?.trade_data?.[periodKey] || {};
const getCount = (row, periodKey) => safeNum(getPeriodObj(row, periodKey)?.total?.count);
const getValue = (row, periodKey) => safeNum(getPeriodObj(row, periodKey)?.total?.value);
const getPending = (row, periodKey) => safeNum(getPeriodObj(row, periodKey)?.pending_amount);
const getProfit = (row, periodKey) => safeNum(getPeriodObj(row, periodKey)?.profit);

const sumAcrossPeriods = (row, keys) => {
    return keys.reduce(
        (acc, k) => {
            acc.count += getCount(row, k);
            acc.value += getValue(row, k);
            acc.pending += getPending(row, k);
            acc.profit += getProfit(row, k);
            return acc;
        },
        { count: 0, value: 0, pending: 0, profit: 0 }
    );
};

const escapeCSV = (value) => {
    const s = String(value ?? "");
    if (s.includes('"') || s.includes(",") || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
    return s;
};

const downloadCSV = (rows, periodView, periodKey, filename = "trade_statistics.csv") => {
    const lines = [];

    if (periodView === "single") {
        const headers = ["entity_id", "entity_name", "period", "trade_count", "total_value", "pending_amount", "profit"];
        lines.push(headers.join(","));
        rows.forEach((r) => {
            const e = r?.entity_info || {};
            lines.push(
                [
                    escapeCSV(e?.entity_id),
                    escapeCSV(e?.entity_name),
                    escapeCSV(periodKey),
                    escapeCSV(getCount(r, periodKey)),
                    escapeCSV(getValue(r, periodKey)),
                    escapeCSV(getPending(r, periodKey)),
                    escapeCSV(getProfit(r, periodKey)),
                ].join(",")
            );
        });
    } else {
        const headers = ["entity_id", "entity_name"];
        PERIODS.forEach((p) => {
            headers.push(`${p.label}_count`, `${p.label}_value`, `${p.label}_pending`, `${p.label}_profit`);
        });
        lines.push(headers.join(","));

        rows.forEach((r) => {
            const e = r?.entity_info || {};
            const row = [escapeCSV(e?.entity_id), escapeCSV(e?.entity_name)];
            PERIODS.forEach((p) => {
                row.push(
                    escapeCSV(getCount(r, p.key)),
                    escapeCSV(getValue(r, p.key)),
                    escapeCSV(getPending(r, p.key)),
                    escapeCSV(getProfit(r, p.key))
                );
            });
            lines.push(row.join(","));
        });
    }

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
};

/* ---------------- component ---------------- */
export default function TradeStatistics() {
    const { logout } = useAuth();

    const today = useMemo(() => getTodayLocalISODate(), []);
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(null);

    const [search, setSearch] = useState("");

    // Period view: "all" shows all periods in cards, "single" shows table with selected period
    const [periodView, setPeriodView] = useState("all"); // "all" | "single"
    const [selectedPeriodKey, setSelectedPeriodKey] = useState("period_30s");

    // For expandable rows in "all" view
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Sort
    const [sortBy, setSortBy] = useState("profit");
    const [sortDir, setSortDir] = useState("asc");

    const visiblePeriodKeys = useMemo(() => {
        if (periodView === "single") return [selectedPeriodKey];
        return PERIOD_KEYS;
    }, [periodView, selectedPeriodKey]);

    const validateDatesOrToast = useCallback((fd, td) => {
        if (!fd || !td) {
            toast.error("From Date and To Date are required");
            return false;
        }
        if (new Date(td) < new Date(fd)) {
            toast.error("To Date must be equal to or after From Date");
            return false;
        }
        return true;
    }, []);

    const fetchTradeStats = useCallback(
        async (opts = {}) => {
            const fd = opts?.from_date ?? fromDate;
            const td = opts?.to_date ?? toDate;

            if (!validateDatesOrToast(fd, td)) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const response = await axios({
                    method: "post",
                    url: `${BASE_ENDPOINT}/admin/statistics/trade`,
                    maxBodyLength: Infinity,
                    headers: { "Content-Type": "application/json" },
                    data: {
                        token: localStorage.getItem("token"),
                        from_date: fd,
                        to_date: td,
                    },
                });

                const data = response?.data;

                if (data?.error) {
                    const errText = String(data?.error || "").toLowerCase();
                    if (errText.includes("session")) {
                        logout();
                        return;
                    }
                    toast.error(data?.msg || data?.error || "Failed to load trade statistics");
                    setRows([]);
                } else {
                    setRows(Array.isArray(data?.data) ? data.data : []);
                    setLastUpdated(new Date());
                    setExpandedRows(new Set()); // Reset expanded rows on new data
                }
            } catch (error) {
                console.log(error);
                toast.error("Failed to load trade statistics");
                setRows([]);
            } finally {
                setLoading(false);
            }
        },
        [fromDate, toDate, validateDatesOrToast, logout]
    );

    useEffect(() => {
        fetchTradeStats({ from_date: today, to_date: today });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [today]);

    const onApplyFilter = async (e) => {
        e.preventDefault();
        await fetchTradeStats({ from_date: fromDate, to_date: toDate });
    };

    const onResetFilter = async () => {
        const t = getTodayLocalISODate();
        setFromDate(t);
        setToDate(t);
        await fetchTradeStats({ from_date: t, to_date: t });
    };

    const totals = useMemo(() => {
        return rows.reduce(
            (acc, r) => {
                const s = sumAcrossPeriods(r, visiblePeriodKeys);
                acc.count += s.count;
                acc.value += s.value;
                acc.pending += s.pending;
                acc.profit += s.profit;
                return acc;
            },
            { count: 0, value: 0, pending: 0, profit: 0 }
        );
    }, [rows, visiblePeriodKeys]);

    const filteredAndSorted = useMemo(() => {
        const q = search.trim().toLowerCase();

        const filtered = !q
            ? rows
            : rows.filter((r) => {
                const e = r?.entity_info || {};
                const name = String(e?.entity_name || "").toLowerCase();
                const id = String(e?.entity_id || "").toLowerCase();
                return name.includes(q) || id.includes(q);
            });

        const getSortVal = (r) => {
            const e = r?.entity_info || {};

            if (sortBy === "name") return String(e?.entity_name || "").toLowerCase();

            if (periodView === "single") {
                switch (sortBy) {
                    case "trade_count":
                        return getCount(r, selectedPeriodKey);
                    case "total_value":
                        return getValue(r, selectedPeriodKey);
                    case "pending_amount":
                        return getPending(r, selectedPeriodKey);
                    case "profit":
                    default:
                        return getProfit(r, selectedPeriodKey);
                }
            }

            // "all" -> sort by sum across all periods
            const s = sumAcrossPeriods(r, visiblePeriodKeys);
            switch (sortBy) {
                case "trade_count":
                    return s.count;
                case "total_value":
                    return s.value;
                case "pending_amount":
                    return s.pending;
                case "profit":
                default:
                    return s.profit;
            }
        };

        const dir = sortDir === "asc" ? 1 : -1;

        return [...filtered].sort((a, b) => {
            const av = getSortVal(a);
            const bv = getSortVal(b);

            if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv) * dir;
            return (safeNum(av) - safeNum(bv)) * dir;
        });
    }, [rows, search, sortBy, sortDir, periodView, selectedPeriodKey, visiblePeriodKeys]);

    const toggleSort = (key) => {
        if (sortBy === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(key);
        }
    };

    const SortIcon = ({ col }) => {
        if (sortBy !== col) return <FaSort className="ms-1 opacity-50" />;
        return sortDir === "asc" ? <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />;
    };

    const toggleRowExpansion = (entityId) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(entityId)) {
            newExpanded.delete(entityId);
        } else {
            newExpanded.add(entityId);
        }
        setExpandedRows(newExpanded);
    };

    // Calculate period totals for "all" view summary
    const periodTotals = useMemo(() => {
        const totals = {};
        PERIODS.forEach(p => {
            totals[p.key] = rows.reduce((acc, row) => {
                acc.count += getCount(row, p.key);
                acc.value += getValue(row, p.key);
                acc.pending += getPending(row, p.key);
                acc.profit += getProfit(row, p.key);
                return acc;
            }, { count: 0, value: 0, pending: 0, profit: 0 });
        });
        return totals;
    }, [rows]);

    return (
        <PageTransition>
            <div className="row g-3">
                {/* Header */}
                <div className="col-12">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div>
                            <h2 className="fw-bold mb-0">Trade Statistics</h2>
                            {lastUpdated ? (
                                <div className="text-muted small">Updated: {lastUpdated.toLocaleString()}</div>
                            ) : (
                                <div className="text-muted small">&nbsp;</div>
                            )}
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() =>
                                    downloadCSV(
                                        filteredAndSorted,
                                        periodView,
                                        selectedPeriodKey,
                                        periodView === "single"
                                            ? `trade_statistics_${selectedPeriodKey}.csv`
                                            : `trade_statistics_all_periods.csv`
                                    )
                                }
                                disabled={loading || filteredAndSorted.length === 0}
                            >
                                <FaDownload className="me-2" />
                                Export
                            </button>

                            <button
                                className="btn btn-primary"
                                type="button"
                                onClick={() => fetchTradeStats({ from_date: fromDate, to_date: toDate })}
                                disabled={loading}
                            >
                                <FaSyncAlt className={loading ? "me-2 spin" : "me-2"} />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filter Form */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 pt-3 pb-2">
                            <div className="fw-semibold">Filters</div>
                        </div>

                        <div className="card-body">
                            <form className="row g-3 align-items-end" onSubmit={onApplyFilter}>
                                <div className="col-12 col-md-4">
                                    <label className="form-label small text-muted mb-1">From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={fromDate}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setFromDate(v);
                                            if (toDate && v && new Date(toDate) < new Date(v)) {
                                                setToDate(v);
                                            }
                                        }}
                                    />
                                </div>

                                <div className="col-12 col-md-4">
                                    <label className="form-label small text-muted mb-1">To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={toDate}
                                        min={fromDate || undefined}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                </div>

                                <div className="col-12 col-md-4">
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                                            Apply
                                        </button>
                                        <button className="btn btn-outline-secondary" type="button" onClick={onResetFilter} disabled={loading}>
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="col-12">
                    <div className="row g-3">
                        <div className="col-12 col-md-6 col-xl-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small">Trades</div>
                                    <div className="fw-bold fs-4">{formatNum(totals.count)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small">Total Value</div>
                                    <div className="fw-bold fs-4">{formatNum(totals.value)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small">Pending</div>
                                    <div className="fw-bold fs-4">{formatNum(totals.pending)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="text-muted small">Profit</div>
                                    <div className={`fw-bold fs-4 ${totals.profit >= 0 ? "text-success" : "text-danger"}`}>
                                        {formatNum(totals.profit)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="input-group" style={{ width: 280 }}>
                                        <span className="input-group-text bg-white">
                                            <FaSearch />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search asset"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>

                                    <div className="btn-group" role="group">
                                        <button
                                            type="button"
                                            className={`btn ${periodView === "all" ? "btn-primary" : "btn-outline-primary"}`}
                                            onClick={() => setPeriodView("all")}
                                            disabled={loading}
                                        >
                                            <FaChartLine className="me-2" />
                                            All Periods
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn ${periodView === "single" ? "btn-primary" : "btn-outline-primary"}`}
                                            onClick={() => setPeriodView("single")}
                                            disabled={loading}
                                        >
                                            Single Period
                                        </button>
                                    </div>

                                    {periodView === "single" && (
                                        <select
                                            className="form-select"
                                            style={{ width: 120 }}
                                            value={selectedPeriodKey}
                                            onChange={(e) => setSelectedPeriodKey(e.target.value)}
                                            disabled={loading}
                                        >
                                            {PERIODS.map((p) => (
                                                <option key={p.key} value={p.key}>
                                                    {p.label}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-muted small">Sort by:</span>
                                    <select
                                        className="form-select"
                                        style={{ width: 160 }}
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value="profit">Profit</option>
                                        <option value="total_value">Total Value</option>
                                        <option value="trade_count">Trade Count</option>
                                        <option value="pending_amount">Pending</option>
                                        <option value="name">Name</option>
                                    </select>
                                    <button
                                        className="btn btn-outline-secondary"
                                        type="button"
                                        onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                        disabled={loading}
                                        title="Toggle Asc/Desc"
                                    >
                                        {sortDir === "asc" ? <FaSortUp /> : <FaSortDown />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table/Cards */}
                <div className="col-12">
                    {loading ? (
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-5 text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <div className="mt-3 text-muted">Loading trade statistics...</div>
                            </div>
                        </div>
                    ) : filteredAndSorted.length === 0 ? (
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-5 text-center">
                                <div className="fw-semibold mb-2">No data found</div>
                                <div className="text-muted">Try adjusting your filters or date range</div>
                            </div>
                        </div>
                    ) : periodView === "single" ? (
                        // Single Period Table View
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light">
                                            <tr className="small text-muted">
                                                <th style={{ width: 50 }}>#</th>
                                                <th>Asset</th>
                                                <th className="text-end" role="button" onClick={() => toggleSort("trade_count")}>
                                                    Trades <SortIcon col="trade_count" />
                                                </th>
                                                <th className="text-end" role="button" onClick={() => toggleSort("total_value")}>
                                                    Value <SortIcon col="total_value" />
                                                </th>
                                                <th className="text-end" role="button" onClick={() => toggleSort("pending_amount")}>
                                                    Pending <SortIcon col="pending_amount" />
                                                </th>
                                                <th className="text-end" role="button" onClick={() => toggleSort("profit")}>
                                                    Profit <SortIcon col="profit" />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAndSorted.map((r, idx) => {
                                                const entity = r?.entity_info || {};
                                                const profit = getProfit(r, selectedPeriodKey);
                                                return (
                                                    <tr key={entity?.entity_id || idx}>
                                                        <td className="text-muted">{idx + 1}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <img
                                                                    src={entity?.image}
                                                                    alt={entity?.entity_name}
                                                                    width="32"
                                                                    height="32"
                                                                    className="rounded-circle border"
                                                                    style={{ objectFit: "cover" }}
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = "none";
                                                                    }}
                                                                />
                                                                <div>
                                                                    <div className="fw-semibold">{entity?.entity_name || "-"}</div>
                                                                    <div className="text-muted small">{entity?.entity_id || "-"}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-end">{formatNum(getCount(r, selectedPeriodKey))}</td>
                                                        <td className="text-end">{formatNum(getValue(r, selectedPeriodKey))}</td>
                                                        <td className="text-end">{formatNum(getPending(r, selectedPeriodKey))}</td>
                                                        <td className={`text-end fw-semibold ${profit >= 0 ? "text-success" : "text-danger"}`}>
                                                            {formatNum(profit)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr className="fw-semibold">
                                                <td colSpan={2}>Totals ({PERIODS.find(p => p.key === selectedPeriodKey)?.label})</td>
                                                <td className="text-end">{formatNum(totals.count)}</td>
                                                <td className="text-end">{formatNum(totals.value)}</td>
                                                <td className="text-end">{formatNum(totals.pending)}</td>
                                                <td className={`text-end ${totals.profit >= 0 ? "text-success" : "text-danger"}`}>
                                                    {formatNum(totals.profit)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // All Periods Card View
                        <div className="row g-3">
                            {filteredAndSorted.map((r, idx) => {
                                const entity = r?.entity_info || {};
                                const entityId = entity?.entity_id || idx;
                                const isExpanded = expandedRows.has(entityId);
                                const summary = sumAcrossPeriods(r, PERIOD_KEYS);

                                return (
                                    <div className="col-12" key={entityId}>
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => toggleRowExpansion(entityId)}
                                                        >
                                                            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                                        </button>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <img
                                                                src={entity?.image}
                                                                alt={entity?.entity_name}
                                                                width="40"
                                                                height="40"
                                                                className="rounded-circle border"
                                                                style={{ objectFit: "cover" }}
                                                                onError={(e) => {
                                                                    e.currentTarget.style.display = "none";
                                                                }}
                                                            />
                                                            <div>
                                                                <div className="fw-bold">{entity?.entity_name || "-"}</div>
                                                                <div className="text-muted small">{entity?.entity_id || "-"}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-4">
                                                        <div className="text-center">
                                                            <div className="text-muted small">Trades</div>
                                                            <div className="fw-bold">{formatNum(summary.count)}</div>
                                                        </div>
                                                        <div className="text-center">
                                                            <div className="text-muted small">Value</div>
                                                            <div className="fw-bold">{formatNum(summary.value)}</div>
                                                        </div>
                                                        <div className="text-center">
                                                            <div className="text-muted small">Pending</div>
                                                            <div className="fw-bold">{formatNum(summary.pending)}</div>
                                                        </div>
                                                        <div className="text-center">
                                                            <div className="text-muted small">Profit</div>
                                                            <div className={`fw-bold ${summary.profit >= 0 ? "text-success" : "text-danger"}`}>
                                                                {formatNum(summary.profit)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {isExpanded && (
                                                    <div className="mt-4 pt-3 border-top">
                                                        <div className="row g-3">
                                                            {PERIODS.map((p) => {
                                                                const profit = getProfit(r, p.key);
                                                                return (
                                                                    <div className="col-12 col-md-4" key={p.key}>
                                                                        <div className="card border">
                                                                            <div className="card-body p-3">
                                                                                <div className="text-center mb-2">
                                                                                    <div className="fw-semibold text-primary">{p.label}</div>
                                                                                </div>
                                                                                <div className="row g-2">
                                                                                    <div className="col-6">
                                                                                        <div className="text-muted small">Trades</div>
                                                                                        <div className="fw-semibold">{formatNum(getCount(r, p.key))}</div>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <div className="text-muted small">Value</div>
                                                                                        <div className="fw-semibold">{formatNum(getValue(r, p.key))}</div>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <div className="text-muted small">Pending</div>
                                                                                        <div className="fw-semibold">{formatNum(getPending(r, p.key))}</div>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <div className="text-muted small">Profit</div>
                                                                                        <div className={`fw-semibold ${profit >= 0 ? "text-success" : "text-danger"}`}>
                                                                                            {formatNum(profit)}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Period Summary */}
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white">
                                        <div className="fw-semibold">Period-wise Totals</div>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3">
                                            {PERIODS.map((p) => {
                                                const total = periodTotals[p.key];
                                                return (
                                                    <div className="col-12 col-md-4" key={p.key}>
                                                        <div className="card border">
                                                            <div className="card-body p-3">
                                                                <div className="text-center mb-2">
                                                                    <div className="fw-bold text-primary">{p.label}</div>
                                                                </div>
                                                                <div className="row g-2">
                                                                    <div className="col-6">
                                                                        <div className="text-muted small">Trades</div>
                                                                        <div className="fw-semibold">{formatNum(total.count)}</div>
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <div className="text-muted small">Value</div>
                                                                        <div className="fw-semibold">{formatNum(total.value)}</div>
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <div className="text-muted small">Pending</div>
                                                                        <div className="fw-semibold">{formatNum(total.pending)}</div>
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <div className="text-muted small">Profit</div>
                                                                        <div className={`fw-semibold ${total.profit >= 0 ? "text-success" : "text-danger"}`}>
                                                                            {formatNum(total.profit)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .spin { animation: spin 0.9s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                th[role="button"] { user-select: none; cursor: pointer; }
                th[role="button"]:hover { opacity: 0.85; }
                .card { transition: all 0.2s ease; }
                .card:hover { transform: translateY(-2px); }
            `}</style>
        </PageTransition>
    );
}